/*
 * product-ring-size-property.js — pseudo "Size" accordion for PDPs
 * whose sizes live in a per-variant `custom.ring_size` JSON array
 * rather than a native Size option (see
 * snippets/product-ring-size-property.liquid +
 * snippets/product-purchase-form-block.liquid).
 *
 * Responsibilities:
 *   1. Read the `.product-purchase-form__ring-size-map` blob
 *      ({variantId: ["H 1/2","N",...]}), fresh on every render so a
 *      combined-listing metal switch (which overwrites that node) is
 *      picked up.
 *   2. Sort UK letter sizes F→T including halves before rendering —
 *      the metafield array arrives unsorted. "H 1/2" and "H.5" are
 *      both accepted.
 *   3. Rebuild the row list on `variant:update` (stone type / metal
 *      change → different size set), preserving the shopper's pick
 *      when it still exists, else defaulting to the first (smallest)
 *      size — matching native variant Size, which always resolves to a
 *      concrete value.
 *   4. Mirror the selected size into the separate configurator /
 *      sticky ATC forms via hidden `[data-ring-size-mirror]` inputs
 *      (the main form's radios submit the property natively).
 *   5. Block add-to-cart when the control is shown and no size is
 *      chosen — open the accordion and surface the inline message.
 *
 * Module-level document listeners (same shape as
 * product-configurator-progress.js) — a single control per PDP.
 */

const HOST_SELECTOR = '[data-ring-size-host]';
const MAP_SELECTOR = '.product-purchase-form__ring-size-map';
const MIRROR_SELECTOR = 'input[data-ring-size-mirror]';
const MAIN_ID_SELECTOR =
  '.product-purchase-form [data-product-form-variant-id], .product-purchase-form [name="id"]';
const PROPERTY_NAME = 'properties[Ring Size]';

/** @type {string | null} — the shopper's chosen size, one source of truth. */
let selectedSize = null;

/**
 * Sort key for a UK ring size: (letter - 'A') * 2, +1 for a half.
 * Non-letter values sort last so junk never reorders real sizes.
 * @param {string} size
 * @returns {number}
 */
function sizeOrder(size) {
  const s = String(size).trim().toUpperCase();
  const code = s.charCodeAt(0);
  if (code < 65 || code > 90) return Number.MAX_SAFE_INTEGER;
  const isHalf = /(?:1\/2|\.5)\s*$/.test(s);
  return (code - 65) * 2 + (isHalf ? 1 : 0);
}

/**
 * @param {unknown} sizes
 * @returns {string[]}
 */
function sortSizes(sizes) {
  if (!Array.isArray(sizes)) return [];
  return sizes
    .filter((s) => typeof s === 'string' && s.trim() !== '')
    .slice()
    .sort((a, b) => sizeOrder(a) - sizeOrder(b));
}

/** @returns {Record<string, string[]>} */
function readMap() {
  const node = document.querySelector(MAP_SELECTOR);
  if (!node?.textContent) return {};
  try {
    const parsed = JSON.parse(node.textContent);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_e) {
    return {};
  }
}

/** @returns {string} */
function currentVariantId() {
  const input = document.querySelector(MAIN_ID_SELECTOR);
  return input?.value ? String(input.value) : '';
}

function host() {
  return document.querySelector(HOST_SELECTOR);
}

function accordion() {
  return host()?.querySelector('[data-ring-size-accordion]') || null;
}

function isVisible() {
  const el = host();
  return !!el && !el.hidden;
}

/**
 * Push the current selection into the configurator / sticky mirror
 * inputs. Disabled (excluded from FormData) when nothing is chosen so
 * an empty `properties[Ring Size]` never reaches the cart.
 */
function syncMirrors() {
  document.querySelectorAll(MIRROR_SELECTOR).forEach((input) => {
    if (selectedSize) {
      input.value = selectedSize;
      input.disabled = false;
    } else {
      input.value = '';
      input.disabled = true;
    }
  });
}

/** @param {string | null} size */
function setSelected(size) {
  selectedSize = size || null;

  const summary = host()?.querySelector('[data-ring-size-summary]');
  if (summary) summary.textContent = selectedSize || '';

  if (selectedSize) hideError();
  syncMirrors();
}

function hideError() {
  const err = host()?.querySelector('[data-ring-size-error]');
  if (err) err.hidden = true;
}

function showError() {
  const acc = accordion();
  if (acc) acc.open = true;

  const err = host()?.querySelector('[data-ring-size-error]');
  if (err) err.hidden = false;

  acc?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * Rebuild the row list for a set of sizes. Keeps the shopper's pick
 * when it survives into the new set; otherwise defaults to the first
 * (smallest) size, mirroring native variant Size which always resolves
 * to a concrete value.
 * @param {string[]} sizes
 * @param {string} formId
 */
function buildRows(sizes, formId) {
  const list = host()?.querySelector('[data-ring-size-list]');
  if (!list) return;

  const keepSelected = selectedSize && sizes.includes(selectedSize)
    ? selectedSize
    : sizes[0] || null;

  const rows = sizes.map((size, index) => {
    const id = `ring-size-${index}`;
    const checked = size === keepSelected ? ' checked' : '';
    const formAttr = formId ? ` form="${formId}"` : '';
    return (
      `<input type="radio" id="${id}" name="${PROPERTY_NAME}"` +
      ` value="${escapeAttr(size)}"${formAttr}` +
      ` class="product-option-accordion__input product-ring-size__input"` +
      ` data-ring-size-input${checked}>` +
      `<label for="${id}" class="product-option-accordion__row">` +
      `<span class="product-option-accordion__row-value">${escapeHtml(size)}</span>` +
      `</label>`
    );
  });

  list.innerHTML = rows.join('');
  setSelected(keepSelected);
}

/** @param {string} value */
function escapeAttr(value) {
  return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Show + populate the control for the current variant, or hide it
 * (and drop any selection) when the current variant has no sizes.
 */
function render() {
  const el = host();
  if (!el) return;

  const sizes = sortSizes(readMap()[currentVariantId()]);

  if (!sizes.length) {
    el.hidden = true;
    hideError();
    const acc = accordion();
    if (acc) acc.open = false;
    setSelected(null);
    const list = el.querySelector('[data-ring-size-list]');
    if (list) list.innerHTML = '';
    return;
  }

  el.hidden = false;
  buildRows(sizes, el.dataset.productFormId || '');
}

function onChange(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  if (!input.hasAttribute('data-ring-size-input') || !input.checked) return;
  setSelected(input.value);
}

/**
 * Gate add-to-cart: when the control is visible and unset, stop the
 * submit before <sa-product-form> (a bubble-phase listener) fetches.
 * Capture phase + stopImmediatePropagation keeps the AJAX add from
 * firing with a missing required property.
 */
function onSubmitCapture(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  // Only guard product add-to-cart forms (they carry the variant id).
  if (!form.querySelector('[name="id"]')) return;
  if (!isVisible() || selectedSize) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  showError();
}

document.addEventListener('change', onChange);
document.addEventListener('submit', onSubmitCapture, true);
document.addEventListener('variant:update', render);
document.addEventListener('product-variants:loaded', render);
document.addEventListener('shopify:section:load', render);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', render);
} else {
  render();
}
